import { ReactElement, ReactNode, createContext, useCallback, useState } from 'react';

import { Modal } from '@/common/components/Modal/Modal';
import { useMountTransition } from '@/common/hooks/useMountTransition/useMountTransition';

const ModalContext = createContext<ModalContextProps>({
  displayModal: () => {
    return;
  },
  closeModal: () => {},
  modalContainerId: 'overlay',
  hasTransitionedIn: false,
  isOpen: false,
});
export default ModalContext;

export interface ModalContextProps {
  displayModal: (content: ReactNode) => void;
  closeModal: () => void;
  modalContainerId?: string;
  hasTransitionedIn: boolean;
  isOpen: boolean;
}

const ModalContextProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const [modalContent, setModalContent] = useState<ReactNode>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [modalContainerId, setModalContainerId] = useState('overlay');

  const displayModal = useCallback((content: ReactNode, containerId?: string) => {
    setModalContent(content);
    setIsOpen(true);
    containerId && setModalContainerId(containerId);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const hasTransitionedIn = useMountTransition(isOpen, 300, () => {
    setModalContent(null);
  });

  return (
    <ModalContext.Provider
      value={{ displayModal, closeModal, modalContainerId, hasTransitionedIn, isOpen }}
    >
      {children}
      {modalContent ? <Modal modalContainerId={modalContainerId}>{modalContent}</Modal> : null}
    </ModalContext.Provider>
  );
};

export { ModalContextProvider };
